import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';
import { LanguageSwitcher } from './LanguageSwitcher';
import { List, X } from '@phosphor-icons/react';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
      setMenuOpen(false);
    }
  };

  const navItems = [
    { id: 'about', label: t.nav.about },
    { id: 'services', label: t.nav.services },
    { id: 'founder', label: t.nav.founder },
    { id: 'team', label: t.nav.team },
    { id: 'contact', label: t.nav.contact },
  ];

  return (
    <motion.header
      data-testid="header"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-black/80 backdrop-blur-xl border-b border-zinc-900' : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3" data-testid="logo">
            <img
              src="/logo.png"
              alt="Neural Brain Team"
              className="w-10 h-10 md:w-12 md:h-12"
            />
            <span className="text-zinc-200 text-sm md:text-base font-light uppercase tracking-[0.2em] hidden sm:block">
              Neural Brain Team
            </span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navItems.map((item) => (
              <button
                key={item.id}
                data-testid={`nav-${item.id}`}
                onClick={() => scrollToSection(item.id)}
                className="text-zinc-400 hover:text-gold transition-colors duration-300 text-sm uppercase tracking-wider"
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Right side: Language + CTA */}
          <div className="flex items-center gap-4 md:gap-6">
            <LanguageSwitcher />

            <button
              data-testid="contact-cta-button"
              onClick={() => scrollToSection('contact')}
              className="hidden md:block bg-gold text-black hover:bg-white transition-colors duration-300 px-6 md:px-8 py-3 md:py-4 uppercase tracking-widest text-xs font-bold"
            >
              {t.header.contactButton}
            </button>

            {/* Mobile menu button */}
            <button
              data-testid="mobile-menu-button"
              onClick={() => setMenuOpen(!menuOpen)}
              className="lg:hidden text-zinc-400 hover:text-gold transition-colors"
            >
              {menuOpen ? <X size={24} weight="thin" /> : <List size={24} weight="thin" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {menuOpen && (
          <motion.nav
            data-testid="mobile-nav"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden mt-6 pt-6 border-t border-zinc-900"
          >
            <div className="flex flex-col gap-4">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  data-testid={`mobile-nav-${item.id}`}
                  onClick={() => scrollToSection(item.id)}
                  className="text-left text-zinc-400 hover:text-gold transition-colors duration-300 text-sm uppercase tracking-wider py-2"
                >
                  {item.label}
                </button>
              ))}
              <button
                data-testid="mobile-contact-cta"
                onClick={() => scrollToSection('contact')}
                className="md:hidden bg-gold text-black hover:bg-white transition-colors duration-300 px-6 py-3 uppercase tracking-widest text-xs font-bold mt-2"
              >
                {t.header.contactButton}
              </button>
            </div>
          </motion.nav>
        )}
      </div>
    </motion.header>
  );
};

export default Header;
